import React, { useState, useEffect, useContext } from 'react';
import {
  FaCog, FaCreditCard, FaTruck, FaBell, FaStore, FaEnvelope,
  FaSms, FaShieldAlt, FaSave, FaUndo, FaToggleOn, FaToggleOff,
  FaPaypal, FaStripe, FaGooglePay, FaApplePay, FaMoneyBillWave, FaGoogle
} from 'react-icons/fa';
import useApi from '../../../../utils/api-manager/Helper/useApi';
import { useToastHandler } from '../../../common/ToastHandler';
import AuthContext from '../../../../utils/secure-route/AuthContext';
import { CustomButton } from '../../../common/ReusableComponent/CustomButton';
import LoadingSpinner from '../Orders/LoadingSpinner';
import useSwal from '../../../common/ReusableComponent/useSwal';
import './EcommerceSettings.css';

const EcommerceSettings = () => {
  const { user } = useContext(AuthContext);

  const store_id = user.store;
  const { _Get, Post, Put, Patch, Get, Delete, getAPI } = useApi();
  const { handleToast } = useToastHandler();
  const { confirm } = useSwal();

  const getStoreConfigErrorMessage = (error, fallbackMessage) => {
    const data = error.response?.data;
    if (data?.conflicts?.length) {
      const domains = data.conflicts
        .flatMap(conflict => conflict.domains || [])
        .filter(Boolean)
        .join(', ');

      return domains
        ? `Domain already assigned to another store: ${domains}`
        : 'One or more domains are already assigned to another store.';
    }

    return (
      data?.message ||
      data?.error ||
      data?.store_id?.[0] ||
      data?.urls?.[0] ||
      fallbackMessage
    );
  };

  const [activeSettingsTab, setActiveSettingsTab] = useState('store');
  const [loading, setLoading] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [editingUrlIndex, setEditingUrlIndex] = useState(null);
  const [editingUrlValue, setEditingUrlValue] = useState('');
  const [savingStore, setSavingStore] = useState(false);
  const [storeConfigId, setStoreConfigId] = useState(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleConfig, setGoogleConfig] = useState({ ga_property_id: '', gsc_site_url: '', gbp_location_id: '' });

  const [settings, setSettings] = useState({
    // General Store Settings
    storeName: '',
    storeDescription: '',
    storeEmail: '',
    storePhone: '',
    storeAddress: '',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
    language: 'en',

    // Store Management
    storeManagement: {
      storeId: '',
      urls: []
    },

    // Payment Settings
    paymentMethods: {
      cod: { enabled: true, name: 'Cash on Delivery' },
      razorpay: { enabled: false, keyId: '', keySecret: '', name: 'Razorpay' },
      stripe: { enabled: false, publishableKey: '', secretKey: '', name: 'Stripe' },
      paypal: { enabled: false, clientId: '', clientSecret: '', name: 'PayPal' },
      paytm: { enabled: false, merchantId: '', merchantKey: '', name: 'Paytm' },
      phonepe: { enabled: false, merchantId: '', saltKey: '', name: 'PhonePe' }
    },

    // Shipping Settings
    shippingMethods: {
      freeShipping: { enabled: true, minAmount: 500, name: 'Free Shipping' },
      flatRate: { enabled: true, rate: 50, name: 'Flat Rate' },
      localDelivery: { enabled: false, rate: 30, radius: 10, name: 'Local Delivery' }
    },

    // Notification Settings
    notifications: {
      orderConfirmation: { email: true, sms: false },
      orderShipped: { email: true, sms: true },
      orderDelivered: { email: true, sms: false },
      lowStock: { email: true, sms: false },
      newCustomer: { email: true, sms: false }
    },

    // Email Settings
    emailSettings: {
      smtpHost: '',
      smtpPort: 587,
      smtpUser: '',
      smtpPassword: '',
      fromEmail: '',
      fromName: ''
    },

    // SMS Settings
    smsSettings: {
      provider: 'twilio',
      apiKey: '',
      apiSecret: '',
      senderId: ''
    },

    // Security Settings
    security: {
      enableSSL: true,
      enableCaptcha: false,
      maxLoginAttempts: 5,
      sessionTimeout: 30,
      enableTwoFactor: false
    },

    // SEO Settings
    seo: {
      metaTitle: '',
      metaDescription: '',
      metaKeywords: '',
      enableSitemap: true,
      enableRobots: true
    }
  });

  const currencies = [
    { value: 'INR', label: '₹ Indian Rupee' },
    { value: 'USD', label: '$ US Dollar' },
    { value: 'EUR', label: '€ Euro' },
    { value: 'GBP', label: '£ British Pound' }
  ];

  const timezones = [
    { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
    { value: 'America/New_York', label: 'America/New_York (EST)' },
    { value: 'Europe/London', label: 'Europe/London (GMT)' },
    { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' }
  ];

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'hi', label: 'Hindi' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' }
  ];

  const fetchGoogleStatus = async () => {
    try {
      const res = await Get(`${getAPI('googleIntegration')}?store=${store_id}`);
      const integration = res?.results?.[0] || res?.data?.[0] || res?.[0] || res;
      if (integration && integration.is_active) {
        setGoogleConnected(true);
        setGoogleConfig({
          ga_property_id: integration.ga_property_id || '',
          gsc_site_url: integration.gsc_site_url || '',
          gbp_location_id: integration.gbp_location_id || ''
        });
      } else {
        setGoogleConnected(false);
      }
    } catch (err) {
      console.error("Error fetching Google status:", err);
    }
  };

  const handleGoogleConnect = async () => {
    try {
      const res = await Get(`${getAPI('googleIntegration')}auth-url/`, { store: store_id });
      if (res?.auth_url) {
        window.location.href = res.auth_url;
      }
    } catch (err) {
      handleToast({ type: 'ERROR', message: 'Failed to initiate Google connection' });
    }
  };

  const handleGoogleSave = async () => {
    setSavingStore(true);
    try {
      const res = await Get(`${getAPI('googleIntegration')}?store=${store_id}`);
      const integration = res?.results?.[0] || res?.data?.[0] || res?.[0] || res;
      
      const payload = {
        store: store_id,
        ga_property_id: googleConfig.ga_property_id,
        gsc_site_url: googleConfig.gsc_site_url,
        gbp_location_id: googleConfig.gbp_location_id,
        is_active: true
      };

      if (integration?.id) {
        await Put(`${getAPI('googleIntegration')}${integration.id}/`, payload);
      } else {
        await Post(getAPI('googleIntegration'), payload);
      }
      handleToast({ type: 'SUCCESS', message: 'Google settings updated successfully!' });
      fetchGoogleStatus();
    } catch (err) {
      handleToast({ type: 'ERROR', message: 'Failed to update Google settings.' });
    } finally {
      setSavingStore(false);
    }
  };

  const handleGoogleDisconnect = async () => {
    const result = await confirm({
      title: 'Disconnect Google Suite?',
      text: 'Are you sure you want to disconnect all Google Analytics, Search Console and Local Business services?',
      icon: 'warning',
      confirmButtonText: 'Yes, disconnect!'
    });
    if (!result.isConfirmed) return;
    
    setSavingStore(true);
    try {
      const res = await Get(`${getAPI('googleIntegration')}?store=${store_id}`);
      const integration = res?.results?.[0] || res?.data?.[0] || res?.[0] || res;
      if (integration?.id) {
        await Patch(`${getAPI('googleIntegration')}${integration.id}/`, { is_active: false });
        setGoogleConnected(false);
        setGoogleConfig({ ga_property_id: '', gsc_site_url: '', gbp_location_id: '' });
        handleToast({ type: 'SUCCESS', message: 'Google account disconnected.' });
      }
    } catch (err) {
      handleToast({ type: 'ERROR', message: 'Failed to disconnect.' });
    } finally {
      setSavingStore(false);
    }
  };

  useEffect(() => {
    if (store_id) {
      fetchStoreConfig();
      fetchGoogleStatus();
    }
  }, [store_id]);

  const fetchStoreConfig = async () => {
    if (!store_id) {
      console.error('[EcommerceSettings] No store_id found in user context');
      handleToast({ 
        type: 'ERROR', 
        message: 'Store ID not found',
        toastId: 'no-store-id'
      });
      return;
    }

    console.group('[EcommerceSettings] Fetching Store Config');
    console.log('Store ID:', store_id);
    console.log('User Context:', user);

    setLoading(true);
    try {
      const domain = window.location.hostname;
      const baseEndpoint = getAPI('ecom_store');
      const endpoint = `${baseEndpoint}?store_id=${store_id}&domain=${domain}`;
      
      console.log('Base Endpoint:', baseEndpoint);
      console.log('Full Endpoint:', endpoint);
      console.log('Making GET request...');

      const response = await Get(endpoint);
      
      console.log('Response received:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', response ? Object.keys(response) : 'null');
      
      if (response) {
        // Handle array response or single object
        const data = Array.isArray(response) 
          ? response.find(item => item.store === store_id || item.store_id === store_id)
          : response;

        if (!data) {
          console.error('❌ STORE MISMATCH: No ecom_store data found for store:', store_id);
          handleToast({ type: 'ERROR', message: 'No configuration found for this store' });
          setLoading(false);
          return;
        }

        console.log('Store Config ID:', data.id);
        console.log('Store URLs (raw):', data.urls);
        
        setStoreConfigId(data.id);
        
        let parsedUrls = [];
        if (data.urls) {
          parsedUrls = Array.isArray(data.urls) 
            ? data.urls 
            : (typeof data.urls === 'string' ? JSON.parse(data.urls) : []);
        }
        
        console.log('Parsed URLs:', parsedUrls);
        
        setSettings(prev => ({
          ...prev,
          storeManagement: {
            storeId: data.store_id || store_id,
            urls: parsedUrls
          }
        }));
        
        console.log('Store config loaded successfully');
      }
      console.groupEnd();
    } catch (error) {
      console.error('[EcommerceSettings] Error fetching store config');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);
      console.groupEnd();
      
      if (error.response?.status === 404) {
        console.warn('[EcommerceSettings] Store config not found (404), initializing empty config');
        // Store config doesn't exist yet, initialize with empty data
        setStoreConfigId(null);
        setSettings(prev => ({
          ...prev,
          storeManagement: {
            storeId: store_id,
            urls: []
          }
        }));
        
        handleToast({ 
          type: 'INFO', 
          message: 'No configuration found for this store. You can add URLs and save.',
          toastId: `no-config-${store_id}`
        });
      } else {
        handleToast({ 
          type: 'ERROR', 
          message: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch store configuration',
          toastId: `fetch-error-${store_id}`
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNestedInputChange = (section, subsection, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  const handleToggle = (section, field) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: !prev[section][field]
      }
    }));
  };

  const handleNestedToggle = (section, subsection, field) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: !prev[section][subsection][field]
        }
      }
    }));
  };

  const resetSettings = async () => {
    const result = await confirm({
      title: 'Reset Configuration',
      text: 'Are you sure you want to reset all URLs? This will clear the current configuration.',
      icon: 'warning',
      confirmButtonText: 'Yes, reset it!',
      confirmButtonColor: '#f59e0b'
    });

    if (!result.isConfirmed) return;

    setSettings(prev => ({
      ...prev,
      storeManagement: {
        storeId: store_id,
        urls: []
      }
    }));
    setNewUrl('');
    cancelEditUrl();
    handleToast({ 
      type: 'INFO', 
      message: 'Form reset successfully',
      toastId: 'reset-form'
    });
  };

  // Store Management Functions
  const addUrl = () => {
    if (!newUrl.trim()) {
      handleToast({ 
        type: 'ERROR', 
        message: 'Please enter a valid URL',
        toastId: 'invalid-url'
      });
      return;
    }

    let formattedUrl = newUrl.trim();

    const urlExists = settings.storeManagement.urls.includes(formattedUrl);
    if (urlExists) {
      handleToast({ 
        type: 'ERROR', 
        message: 'URL already exists',
        toastId: 'url-exists'
      });
      return;
    }

    setSettings(prev => ({
      ...prev,
      storeManagement: {
        ...prev.storeManagement,
        urls: [...prev.storeManagement.urls, formattedUrl]
      }
    }));
    setNewUrl('');
  };

  const startEditUrl = (index, url) => {
    setEditingUrlIndex(index);
    setEditingUrlValue(url);
  };

  const cancelEditUrl = () => {
    setEditingUrlIndex(null);
    setEditingUrlValue('');
  };

  const saveEditedUrl = async (index) => {
    const formattedUrl = editingUrlValue.trim();
    if (!formattedUrl) {
      handleToast({
        type: 'ERROR',
        message: 'Please enter a valid URL',
        toastId: 'invalid-edit-url'
      });
      return;
    }

    const duplicateUrl = settings.storeManagement.urls.some(
      (url, urlIndex) => urlIndex !== index && url === formattedUrl
    );
    if (duplicateUrl) {
      handleToast({
        type: 'ERROR',
        message: 'URL already exists',
        toastId: 'edit-url-exists'
      });
      return;
    }

    const updatedUrls = settings.storeManagement.urls.map((url, urlIndex) =>
      urlIndex === index ? formattedUrl : url
    );

    if (!storeConfigId) {
      setSettings(prev => ({
        ...prev,
        storeManagement: {
          ...prev.storeManagement,
          urls: updatedUrls
        }
      }));
      cancelEditUrl();
      handleToast({
        type: 'INFO',
        message: 'URL updated. Click Save to persist changes.',
        toastId: 'url-edited-local'
      });
      return;
    }

    setSavingStore(true);
    try {
      await Patch('ecom_store', storeConfigId, { urls: updatedUrls });
      setSettings(prev => ({
        ...prev,
        storeManagement: {
          ...prev.storeManagement,
          urls: updatedUrls
        }
      }));
      cancelEditUrl();
      handleToast({
        type: 'SUCCESS',
        message: 'URL updated successfully',
        toastId: `edit-url-${Date.now()}`
      });
      await fetchStoreConfig();
    } catch (error) {
      const errorMessage = getStoreConfigErrorMessage(error, 'Failed to update URL');
      handleToast({
        type: 'ERROR',
        message: errorMessage,
        toastId: `edit-error-${Date.now()}`
      });
    } finally {
      setSavingStore(false);
    }
  };

  const removeUrl = async (urlToRemove) => {
    const result = await confirm({
      title: 'Delete URL',
      text: 'Are you sure you want to delete this URL?',
      icon: 'warning',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;

    if (!storeConfigId) {
      const updatedUrls = settings.storeManagement.urls.filter(url => url !== urlToRemove);
      setSettings(prev => ({
        ...prev,
        storeManagement: {
          ...prev.storeManagement,
          urls: updatedUrls
        }
      }));
      handleToast({ 
        type: 'INFO', 
        message: 'URL removed. Click Save to persist changes.',
        toastId: 'url-removed'
      });
      return;
    }

    setSavingStore(true);
    try {
      const updatedUrls = settings.storeManagement.urls.filter(url => url !== urlToRemove);
      
      console.log('[EcommerceSettings] Deleting URL:', urlToRemove);
      console.log('[EcommerceSettings] Store Config ID:', storeConfigId);
      console.log('[EcommerceSettings] Updated URLs array:', updatedUrls);
      
      const payload = { urls: updatedUrls };
      
      await Patch('ecom_store', storeConfigId, payload);
      
      setSettings(prev => ({
        ...prev,
        storeManagement: {
          ...prev.storeManagement,
          urls: updatedUrls
        }
      }));
      
      if (updatedUrls.length === 0) {
        setStoreConfigId(null);
      }
      
      handleToast({ 
        type: 'SUCCESS', 
        message: 'URL deleted successfully',
        toastId: `delete-url-${Date.now()}`
      });
      
      await fetchStoreConfig();
    } catch (error) {
      console.error('[EcommerceSettings] Error deleting URL:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = getStoreConfigErrorMessage(error, 'Failed to delete URL');
      handleToast({ 
        type: 'ERROR', 
        message: errorMessage,
        toastId: `delete-error-${Date.now()}`
      });
      await fetchStoreConfig();
    } finally {
      setSavingStore(false);
    }
  };

  const saveStoreConfig = async () => {
    if (!store_id) {
      console.error('[EcommerceSettings] No store_id for saving');
      handleToast({ 
        type: 'ERROR', 
        message: 'Store ID not found in user context',
        toastId: 'no-store-id'
      });
      return;
    }

    console.group('[EcommerceSettings] Saving Store Config');
    console.log('Store ID:', store_id);
    console.log('Store Config ID:', storeConfigId);
    console.log('URLs to save:', settings.storeManagement.urls);

    setSavingStore(true);
    try {
      const payload = {
        store_id: String(store_id),
        urls: settings.storeManagement.urls
      };

      console.log('Payload:', JSON.stringify(payload, null, 2));
      console.log('API Endpoint:', getAPI('ecom_store'));

      const response = await Post('ecom_store', payload);

      console.log('Save response:', response);
      console.log('Response data:', response?.data);

      if (response?.data) {
        const responseData = response.data.data || response.data;
        
        console.log('Response data parsed:', responseData);
        
        if (responseData.id) {
          setStoreConfigId(responseData.id);
          console.log('Store config ID updated:', responseData.id);
        }

        handleToast({
          type: 'SUCCESS',
          message: response.data.message || 'Store configuration saved successfully!',
          toastId: `save-success-${Date.now()}`
        });

        console.log('Refreshing store config...');
        await fetchStoreConfig();
        console.log('Store config refreshed');
      }
      console.groupEnd();
    } catch (error) {
      console.error('[EcommerceSettings] Error saving store config');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.groupEnd();
      
      const errorMessage = getStoreConfigErrorMessage(error, 'Failed to save store configuration');
      handleToast({ 
        type: 'ERROR', 
        message: errorMessage,
        toastId: `save-error-${Date.now()}`
      });
    } finally {
      setSavingStore(false);
    }
  };

  const settingsTabs = [
    { id: 'store', label: 'Store Management', icon: <FaStore />, disabled: false },
    { id: 'google', label: 'Google Integrations', icon: <FaGoogle />, disabled: false },
    { id: 'general', label: 'General', icon: <FaStore />, disabled: true },
    { id: 'payment', label: 'Payment', icon: <FaCreditCard />, disabled: true },
    { id: 'shipping', label: 'Shipping', icon: <FaTruck />, disabled: true },
    { id: 'notifications', label: 'Notifications', icon: <FaBell />, disabled: true },
    { id: 'email', label: 'Email', icon: <FaEnvelope />, disabled: true },
    { id: 'sms', label: 'SMS', icon: <FaSms />, disabled: true },
    { id: 'security', label: 'Security', icon: <FaShieldAlt />, disabled: true }
  ];

  return (
    <div className="ecommerce-settings-container">
      {savingStore && <LoadingSpinner />}

      <div className="settings-header">
        <h3><FaCog /> E-commerce Settings</h3>
      </div>

      <div className="settings-content">
        <div className="settings-tabs">
          {settingsTabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab-btn ${activeSettingsTab === tab.id ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
              onClick={() => !tab.disabled && setActiveSettingsTab(tab.id)}
              disabled={tab.disabled}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div className="settings-panel">
          {activeSettingsTab === 'store' && (
            <div className="settings-section">
              <div className="store-management-header">
                <h4>Store Management</h4>
                <p className="section-description">Configure your store settings and manage associated URLs</p>
              </div>

              <div className="store-management">
                <div className="store-info-card">
                  <div className="card-header-set">
                    <h2>Store Information</h2>
                  </div>
                  <div className="form-group-settings">
                    <label>Store ID</label>
                    <div className="input-with-icon">
                      <input
                        type="text"
                        value={store_id}
                        onChange={(e) => handleInputChange('storeManagement', 'storeId', e.target.value)}
                        placeholder="Enter your store ID (e.g., 1ee54283-d02b-4524-8d0d-c7d65e64db48)"
                        disabled
                      />
                      <span className="input-status">Auto-filled from user context</span>
                    </div>
                  </div>
                </div>

                <div className="url-management-card">
                  <div className="card-header-set">
                    <h2>Store URLs Management</h2>
                    <p className="card-description">Add and manage URLs associated with your store</p>
                  </div>

                  <div className="url-input-container">
                    <label>Add New URL</label>
                    <div className="url-input-section">
                      <input
                        type="text"
                        value={newUrl}
                        onChange={(e) => setNewUrl(e.target.value)}
                        placeholder="Enter store URL (e.g., mystore.com or https://mystore.com)"
                        onKeyPress={(e) => e.key === 'Enter' && addUrl()}
                      />
                      <button
                        type="button"
                        className="add-url-btn"
                        onClick={addUrl}
                      >
                        <span>+</span> Add URL
                      </button>
                    </div>
                  </div>

                  {settings.storeManagement.urls.length > 0 && (
                    <div className="urls-list-container">
                      <div className="urls-list-header">
                        <h6>Active URLs ({settings.storeManagement.urls.length})</h6>
                      </div>
                      <div className="urls-list">
                        {settings.storeManagement.urls.map((url, index) => (
                          <div key={index} className="url-item">
                            {editingUrlIndex === index ? (
                              <>
                                <input
                                  className="url-edit-input"
                                  value={editingUrlValue}
                                  onChange={(e) => setEditingUrlValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveEditedUrl(index);
                                    if (e.key === 'Escape') cancelEditUrl();
                                  }}
                                  autoFocus
                                />
                                <div className="url-actions">
                                  <button
                                    type="button"
                                    className="save-url-btn"
                                    onClick={() => saveEditedUrl(index)}
                                    disabled={savingStore}
                                    title="Save URL"
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    className="cancel-url-btn"
                                    onClick={cancelEditUrl}
                                    disabled={savingStore}
                                    title="Cancel edit"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="url-info">
                                  <span className="url-text">{url}</span>
                                  <span className="url-index">#{index + 1}</span>
                                </div>
                                <div className="url-actions">
                                  <button
                                    type="button"
                                    className="edit-url-btn"
                                    onClick={() => startEditUrl(index, url)}
                                    title="Edit URL"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="remove-url-btn"
                                    onClick={() => removeUrl(url)}
                                    title="Remove URL"
                                  >
                                    ×
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {settings.storeManagement.urls.length === 0 && (
                    <div className="empty-state">
                      <div className="empty-icon">🔗</div>
                      <p>No URLs added yet</p>
                      <span>Add your first store URL above</span>
                    </div>
                  )}
                </div>

                <div className="store-actions">
                  <CustomButton
                    text="Reset"
                    onClick={resetSettings}
                    backgroundColor="#6b7280"
                    color="#ffffff"
                  />
                  <CustomButton
                    text={savingStore ? 'Saving...' : 'Save Store Configuration'}
                    onClick={saveStoreConfig}
                    disabled={savingStore}
                    backgroundColor="rgb(74, 85, 70)"
                    color="#ffffff"
                  />
                </div>
              </div>
            </div>
          )}
          {activeSettingsTab === 'general' && (
            <div className="settings-section">
              <h4>General Store Settings</h4>
              <p className="disabled-message">This section is currently disabled.</p>
            </div>
          )}

          {activeSettingsTab === 'payment' && (
            <div className="settings-section">
              <h4>Payment Methods</h4>
              <p className="disabled-message">This section is currently disabled.</p>
            </div>
          )}

          {activeSettingsTab === 'shipping' && (
            <div className="settings-section">
              <h4>Shipping Methods</h4>
              <p className="disabled-message">This section is currently disabled.</p>
            </div>
          )}

          {activeSettingsTab === 'notifications' && (
            <div className="settings-section">
              <h4>Notification Settings</h4>
              <p className="disabled-message">This section is currently disabled.</p>
            </div>
          )}

          {activeSettingsTab === 'email' && (
            <div className="settings-section">
              <h4>Email Configuration</h4>
              <p className="disabled-message">This section is currently disabled.</p>
            </div>
          )}

          {activeSettingsTab === 'sms' && (
            <div className="settings-section">
              <h4>SMS Configuration</h4>
              <p className="disabled-message">This section is currently disabled.</p>
            </div>
          )}

          {activeSettingsTab === 'security' && (
            <div className="settings-section">
              <h4>Security Settings</h4>
              <p className="disabled-message">This section is currently disabled.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EcommerceSettings;
